import { performanceReportingApiUrl, fetcher, handleError, ServerErrorResponse } from 'data_sources/constants';
import { CreateTemplateRequest, Template, TemplateStatus } from '../_types';

export default function useManageTemplates() {
  const createTemplate = async (
    data: CreateTemplateRequest,
    callback?: (data: Template) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${performanceReportingApiUrl()}/performance/templates/v1/templates`;
    await fetcher({
      url,
      method: 'POST',
      body: data,
    })
      .then((response) => {
        if (response.errorDetails) {
          // Pass the error response directly to onError instead of throwing
          if (onError) onError(response);
          return;
        }
        if (callback) callback(response);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const updateTemplate = async (
    template: Template,
    changeNote?: string,
    callback?: (data: Template) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${performanceReportingApiUrl()}/performance/templates/v1/templates/${template.id}`;
    const requestBody = {
      ...template,
      ...(changeNote && { changeNote }),
    };

    await fetcher({
      url,
      method: 'PUT',
      body: requestBody,
    })
      .then((response) => {
        if (response.errorDetails) {
          if (onError) onError(response);
          return;
        }
        if (callback) callback(response);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const publishTemplate = async (
    templateId: string,
    payload: { changeNote?: string; reviewWorkflowId?: number; updatedBy?: string },
    callback?: (data: Template) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${performanceReportingApiUrl()}/performance/templates/v1/templates/${templateId}/publish`;
    await fetcher({
      url,
      method: 'POST',
      body: payload,
    })
      .then((response) => {
        if (response.errorDetails) {
          if (onError) onError(response);
          return;
        }
        if (callback) callback(response);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const deactivateTemplate = async (
    templateId: number,
    templateVersionId: number,
    callback?: (data: any) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${performanceReportingApiUrl()}/performance/templates/v1/templates/${templateId}/deactivate`;
    await fetcher({
      url,
      method: 'DELETE',
      body: { templateVersionId },
    })
      .then((response) => {
        if (response.errorDetails) {
          if (onError) onError(response);
          return;
        }
        if (callback) callback(response);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const duplicateTemplate = async (
    templateId: string,
    newName: string,
    callback?: (data: Template) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${performanceReportingApiUrl()}/performance/templates/v1/templates/${templateId}/duplicate`;
    await fetcher({
      url,
      method: 'POST',
      body: { name: newName },
    })
      .then((response) => {
        if (response.errorDetails) {
          if (onError) onError(response);
          return;
        }
        if (callback) callback(response);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const updateTemplateStatus = async (
    templateId: string,
    status: TemplateStatus,
    callback?: (data: Template) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${performanceReportingApiUrl()}/performance/templates/v1/templates/${templateId}/status`;
    await fetcher({
      url,
      method: 'PATCH',
      body: { status },
    })
      .then((response) => {
        if (response.errorDetails) {
          if (onError) onError(response);
          return;
        }
        if (callback) callback(response);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const resetTemplate = async (
    templateId: string,
    callback?: (data: Template) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${performanceReportingApiUrl()}/performance/templates/v1/templates/${templateId}/reset`;
    await fetcher({
      url,
      method: 'POST',
      body: {},
    })
      .then((response) => {
        if (response.errorDetails) {
          if (onError) onError(response);
          return;
        }
        if (callback) callback(response);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  return {
    createTemplate,
    updateTemplate,
    publishTemplate,
    deactivateTemplate,
    duplicateTemplate,
    updateTemplateStatus,
    resetTemplate,
  };
}
